import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Car, Shield, Lock, BadgeCheck, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const AuthPage = () => {
  const { API, handleLogin } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Login form
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Register form
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    user_type: 'client',
    phone: '',
    address: '',
    city: '',
    company_name: '',
    services_offered: []
  });

  const serviceOptions = [
    'Cambio de aceite',
    'Cambio de frenos',
    'Cambio de llantas',
    'Alineación',
    'Aire acondicionado',
    'Enderezado y pintura',
    'Mecánica general',
    'Diagnóstico'
  ];

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      const { session_token, user } = response.data;

      handleLogin(user, session_token);
      toast.success('Inicio de sesión exitoso');

      // Redirect to appropriate dashboard
      if (user.user_type === 'client') {
        navigate('/client/dashboard');
      } else if (user.user_type === 'taller') {
        navigate('/taller/dashboard');
      } else {
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        email: registerData.email,
        password: registerData.password,
        name: registerData.name,
        user_type: registerData.user_type,
        phone: registerData.phone,
        location: {
          address: registerData.address,
          city: registerData.city,
          country: 'Costa Rica'
        }
      };

      if (registerData.user_type === 'taller') {
        data.company_name = registerData.company_name;
        data.services_offered = registerData.services_offered;
      }

      const response = await axios.post(`${API}/auth/register`, data);
      const { session_token, user } = response.data;

      handleLogin(user, session_token);
      toast.success('Registro exitoso');

      // Redirect to appropriate dashboard
      if (user.user_type === 'client') {
        navigate('/client/dashboard');
      } else if (user.user_type === 'taller') {
        navigate('/taller/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-5">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:col-span-2 flex-col justify-between bg-slate-950 passport-dark-grid text-white p-12">
        <button onClick={() => navigate('/')} className="flex items-center space-x-3 w-fit" data-testid="brand-logo-btn">
          <div className="w-11 h-11 bg-purple-600 rounded-xl flex items-center justify-center">
            <Car className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold font-heading">Flix</span>
        </button>

        <div>
          <h1 className="text-4xl font-bold leading-tight mb-6">
            La manera inteligente y <span className="text-purple-400">segura</span> de cuidar tu vehículo
          </h1>
          <p className="text-slate-400 leading-relaxed mb-10">
            Talleres certificados, ofertas transparentes y un historial vehicular inmutable con Car Passport.
          </p>
          <ul className="space-y-4">
            {[
              ['Talleres verificados', 'Cada taller pasa por un proceso de certificación.'],
              ['Historial protegido', 'Tus servicios quedan en un registro inmutable.'],
              ['Ofertas transparentes', 'Compara precios y reputación antes de decidir.'],
            ].map(([title, desc]) => (
              <li key={title} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                <span className="text-sm text-slate-300"><strong className="text-white">{title}.</strong> {desc}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-6 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Datos protegidos</span>
          <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Conexión segura</span>
          <span className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5" /> Plataforma certificada</span>
        </div>
      </div>

      {/* Form panel */}
      <div className="lg:col-span-3 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex lg:hidden items-center justify-center space-x-3 mb-8">
            <div className="w-11 h-11 bg-purple-600 rounded-xl flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-3xl font-bold text-slate-900 font-heading">Flix</span>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Bienvenido a Flix</h2>
            <p className="text-sm text-slate-500 mt-1">Ingresa a tu cuenta o crea una nueva para comenzar</p>
          </div>

        <Card className="p-6 border border-slate-200 shadow-sm rounded-2xl">
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 bg-slate-100 rounded-lg p-1">
              <TabsTrigger value="login" className="rounded-md data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow-sm font-medium">
                Iniciar Sesión
              </TabsTrigger>
              <TabsTrigger value="register" className="rounded-md data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow-sm font-medium">
                Registrarse
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLoginSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-700 font-medium">Email</Label>
                  <Input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="border-slate-200 mt-1.5"
                    data-testid="login-email"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Contraseña</Label>
                  <Input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="border-slate-200 mt-1.5"
                    data-testid="login-password"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-purple-600 hover:bg-purple-700 active:scale-95 transition-transform" 
                  disabled={loading} 
                  data-testid="login-submit"
                >
                  {loading ? 'Iniciando...' : 'Iniciar Sesión'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegisterSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-700 font-medium">Tipo de Usuario</Label>
                  <Select
                    value={registerData.user_type}
                    onValueChange={(value) => setRegisterData({ ...registerData, user_type: value })}
                  >
                    <SelectTrigger className="border-slate-200 mt-1.5" data-testid="register-user-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Cliente</SelectItem>
                      <SelectItem value="taller">Taller</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Nombre {registerData.user_type === 'client' ? 'Completo' : 'del Taller'}</Label>
                  <Input
                    value={registerData.name}
                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                    required
                    className="border-slate-200 mt-1.5"
                    data-testid="register-name"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Email</Label>
                  <Input
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="border-slate-200 mt-1.5"
                    data-testid="register-email"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Contraseña</Label>
                  <Input
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    minLength={6}
                    className="border-slate-200 mt-1.5"
                    data-testid="register-password"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Teléfono</Label>
                  <Input
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                    placeholder="+506 1234-5678"
                    className="border-slate-200 mt-1.5"
                    data-testid="register-phone"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Dirección</Label>
                  <Input
                    value={registerData.address}
                    onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                    className="border-slate-200 mt-1.5"
                    data-testid="register-address"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Ciudad</Label>
                  <Input
                    value={registerData.city}
                    onChange={(e) => setRegisterData({ ...registerData, city: e.target.value })}
                    placeholder="San José"
                    className="border-slate-200 mt-1.5"
                    data-testid="register-city"
                  />
                </div>

                {registerData.user_type === 'taller' && (
                  <>
                    <div>
                      <Label className="text-slate-700 font-medium">Nombre de la Empresa</Label>
                      <Input
                        value={registerData.company_name}
                        onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                        className="border-slate-200 mt-1.5"
                        data-testid="register-company-name"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-700 font-medium mb-2 block">Servicios Ofrecidos</Label>
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-slate-200 rounded-lg">
                        {serviceOptions.map((service) => (
                          <label key={service} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-purple-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={registerData.services_offered.includes(service)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setRegisterData({
                                    ...registerData,
                                    services_offered: [...registerData.services_offered, service]
                                  });
                                } else {
                                  setRegisterData({
                                    ...registerData,
                                    services_offered: registerData.services_offered.filter(s => s !== service)
                                  });
                                }
                              }}
                              className="rounded border-purple-300 text-purple-600 focus:ring-purple-600"
                            />
                            <span className="text-slate-700">{service}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-purple-600 hover:bg-purple-700 active:scale-95 transition-transform" 
                  disabled={loading} 
                  data-testid="register-submit"
                >
                  {loading ? 'Registrando...' : 'Registrarse'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>

          <div className="flex items-center justify-between mt-4">
            <Button
              variant="link"
              onClick={() => navigate('/')}
              className="text-slate-500 hover:text-purple-700 px-0"
              data-testid="back-to-home"
            >
              ← Volver al inicio
            </Button>
            <span className="flex items-center gap-1.5 text-xs text-slate-400">
              <Lock className="w-3 h-3" /> Conexión cifrada
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
